import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import IotService from "./IotService";
import type {
    IotObjekt,
    IotObjektDetail,
    CreateIotObjektRequest,
    UpdateIotObjektRequest,
    IotSenzor,
    UpdateIotSenzorRequest,
    IotMuteRequest,
    IotMuteExtendedRequest,
    IotMuteBatchRequest,
    IotMuteResponse,
    IotSenzorUdalost,
    IotPrijemce,
    JednotkaPrijemce,
    CreateIotPrijemceRequest,
    IotDashboardResponse,
    IotTeplotaResponse,
    IotSenzorTypKonfig,
    CreateIotSenzorTypRequest,
    UpdateIotSenzorTypRequest,
    MqttCredentialsResponse,
    SenzorCapabilities,
    FeatureCommandRequest,
    FeatureCommandResponse,
    IotDetekceStavResponse,
    DisableDetekceRequest,
} from "../generated";

// =====================
// QUERY KEYS
// =====================

export const IOT_QUERY_KEYS = {
    objekty: ["iotObjekty"] as const,
    objekt: (id: number) => ["iotObjekt", id] as const,
    senzor: (id: number) => ["iotSenzor", id] as const,
    senzorHistorie: (id: number) => ["iotSenzorHistorie", id] as const,
    senzorTeplota: (id: number, limit: number) => ["iotSenzorTeplota", id, limit] as const,
    prijemci: (objektId: number) => ["iotPrijemci", objektId] as const,
    globalniPrijemci: ["iotGlobalniPrijemci"] as const,
    dashboard: ["iotDashboard"] as const,
    senzorTypy: ["iotSenzorTypy"] as const,
    vychoziSablony: ["iotSenzorTypyVychoziSablony"] as const,
    senzorTyp: (id: number) => ["iotSenzorTyp", id] as const,
    mqttCredentials: ["iotMqttCredentials"] as const,
    capabilities: (objektId: number) => ["iotCapabilities", objektId] as const,
    detekceStav: ["iotDetekceStav"] as const,
};

// =====================
// QUERIES - OBJEKTY
// =====================

export const useIotObjektyQuery = () => {
    return useQuery<IotObjekt[], Error>({
        queryKey: IOT_QUERY_KEYS.objekty,
        queryFn: () => IotService.getAllObjekty(),
    });
};

export const useIotObjektQuery = (id: number) => {
    return useQuery<IotObjektDetail, Error>({
        queryKey: IOT_QUERY_KEYS.objekt(id),
        queryFn: () => IotService.getObjektById(id),
        enabled: !!id,
    });
};

// =====================
// QUERIES - SENZORY
// =====================

export const useIotSenzorQuery = (id: number) => {
    return useQuery<IotSenzor, Error>({
        queryKey: IOT_QUERY_KEYS.senzor(id),
        queryFn: () => IotService.getSenzorById(id),
        enabled: !!id,
    });
};

export const useIotSenzorHistorieQuery = (id: number, limit = 100) => {
    return useQuery<IotSenzorUdalost[], Error>({
        queryKey: IOT_QUERY_KEYS.senzorHistorie(id),
        queryFn: () => IotService.getSenzorHistorie(id, limit),
        enabled: !!id,
    });
};

// 288 = pocet vzorku odpovidajici ~24h pri BE defaultnim 5min intervalu heartbeatu (viz BE komentar)
export const useIotSenzorTeplotaQuery = (id: number, limit = 288) => {
    return useQuery<IotTeplotaResponse, Error>({
        queryKey: IOT_QUERY_KEYS.senzorTeplota(id, limit),
        queryFn: () => IotService.getSenzorTeplota(id, limit),
        enabled: !!id,
    });
};

// =====================
// QUERIES - PRIJEMCI
// =====================

export const useIotPrijemciQuery = (objektId: number) => {
    return useQuery<IotPrijemce[], Error>({
        queryKey: IOT_QUERY_KEYS.prijemci(objektId),
        queryFn: () => IotService.getObjektPrijemci(objektId),
        enabled: !!objektId,
    });
};

// =====================
// QUERIES - GLOBALNI PRIJEMCI
// =====================

export const useGlobalniPrijemciQuery = () => {
    return useQuery<JednotkaPrijemce[], Error>({
        queryKey: IOT_QUERY_KEYS.globalniPrijemci,
        queryFn: () => IotService.getGlobalniPrijemci(),
    });
};

// =====================
// QUERIES - DASHBOARD
// =====================

export const useIotDashboardQuery = () => {
    return useQuery<IotDashboardResponse, Error>({
        queryKey: IOT_QUERY_KEYS.dashboard,
        queryFn: () => IotService.getDashboard(),
        refetchInterval: 30000, // Refresh every 30s
    });
};

// =====================
// MUTATIONS - OBJEKTY
// =====================

export const useCreateIotObjektMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<IotObjekt, Error, CreateIotObjektRequest>({
        mutationFn: (data) => IotService.createObjekt(data),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.objekty });
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.dashboard });
            showToast("Objekt byl vytvořen", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vytvořit objekt", "error");
        },
    });
};

export const useUpdateIotObjektMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<IotObjekt, Error, { id: number; data: UpdateIotObjektRequest }>({
        mutationFn: ({ id, data }) => IotService.updateObjekt(id, data),

        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.objekty });
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.objekt(id) });
            showToast("Objekt byl aktualizován", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat objekt", "error");
        },
    });
};

export const useDeleteIotObjektMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (id) => IotService.deleteObjekt(id),

        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.objekty });
            queryClient.removeQueries({ queryKey: IOT_QUERY_KEYS.objekt(id) });
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.dashboard });
            showToast("Objekt byl smazán", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat objekt", "error");
        },
    });
};

// =====================
// MUTATIONS - SENZORY
// =====================

export const useUpdateIotSenzorMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<IotSenzor, Error, { id: number; data: UpdateIotSenzorRequest }>({
        mutationFn: ({ id, data }) => IotService.updateSenzor(id, data),

        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.senzor(result.id) });
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.objekt(result.objektId) });
            showToast("Senzor byl aktualizován", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat senzor", "error");
        },
    });
};

export const useMuteIotSenzorMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<IotSenzor, Error, { id: number; data: IotMuteRequest | IotMuteExtendedRequest }>({
        mutationFn: ({ id, data }) => IotService.muteSenzor(id, data),

        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.senzor(result.id) });
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.objekt(result.objektId) });
            const muteDo = result.muteDo ? new Date(result.muteDo).toLocaleString('cs-CZ') : '';
            showToast(`Senzor dočasně ztlumen do ${muteDo}`, "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se ztlumit senzor", "error");
        },
    });
};

export const useUnmuteIotSenzorMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<IotSenzor, Error, number>({
        mutationFn: (id) => IotService.unmuteSenzor(id),

        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.senzor(result.id) });
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.objekt(result.objektId) });
            showToast("Ztlumení senzoru bylo zrušeno", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se zrušit ztlumení", "error");
        },
    });
};

// =====================
// MUTATIONS - HROMADNE MUTE
// =====================

export const useMuteBatchSenzoryMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<IotMuteResponse, Error, { objektId: number; data: IotMuteBatchRequest }>({
        mutationFn: ({ data }) => IotService.muteBatchSenzory(data),

        onSuccess: (result, { objektId }) => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.objekt(objektId) });
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.dashboard });
            showToast(result.message || `Ztlumeno ${result.count} senzorů`, "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se ztlumit senzory", "error");
        },
    });
};

export const useMuteObjektMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<IotMuteResponse, Error, { objektId: number; data: IotMuteExtendedRequest }>({
        mutationFn: ({ objektId, data }) => IotService.muteObjekt(objektId, data),

        onSuccess: (result, { objektId }) => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.objekt(objektId) });
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.dashboard });
            showToast(result.message || `Ztlumeno ${result.count} senzorů v objektu`, "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se ztlumit objekt", "error");
        },
    });
};

export const useUnmuteObjektMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<IotMuteResponse, Error, number>({
        mutationFn: (objektId) => IotService.unmuteObjekt(objektId),

        onSuccess: (result, objektId) => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.objekt(objektId) });
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.dashboard });
            showToast(result.message || `Zrušeno ztlumení ${result.count} senzorů`, "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se zrušit ztlumení objektu", "error");
        },
    });
};

// =====================
// MUTATIONS - SIRENA
// =====================

export const useStartSirenaMutation = () => {
    const { showToast } = useToast();

    return useMutation<void, Error, { objektId: number; senzorId: number }>({
        mutationFn: ({ objektId, senzorId }) => IotService.startSirena(objektId, senzorId),

        onSuccess: () => {
            showToast("Siréna spuštěna", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se spustit sirénu", "error");
        },
    });
};

export const useStopSirenaMutation = () => {
    const { showToast } = useToast();

    return useMutation<void, Error, { objektId: number; senzorId: number }>({
        mutationFn: ({ objektId, senzorId }) => IotService.stopSirena(objektId, senzorId),

        onSuccess: () => {
            showToast("Siréna zastavena", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se zastavit sirénu", "error");
        },
    });
};

// =====================
// MUTATIONS - PRIJEMCI
// =====================

export const useAddIotPrijemceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<IotPrijemce, Error, { objektId: number; data: CreateIotPrijemceRequest }>({
        mutationFn: ({ objektId, data }) => IotService.addObjektPrijemce(objektId, data),

        onSuccess: (_, { objektId }) => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.prijemci(objektId) });
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.objekt(objektId) });
            showToast("Příjemce byl přidán", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se přidat příjemce", "error");
        },
    });
};

export const useRemoveIotPrijemceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { objektId: number; prijemceId: number }>({
        mutationFn: ({ objektId, prijemceId }) => IotService.removeObjektPrijemce(objektId, prijemceId),

        onSuccess: (_, { objektId }) => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.prijemci(objektId) });
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.objekt(objektId) });
            showToast("Příjemce byl odebrán", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se odebrat příjemce", "error");
        },
    });
};

// =====================
// MUTATIONS - GLOBALNI PRIJEMCI
// =====================

export const useAddGlobalniPrijemceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<JednotkaPrijemce, Error, CreateIotPrijemceRequest>({
        mutationFn: (data) => IotService.addGlobalniPrijemce(data),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.globalniPrijemci });
            showToast("Globální příjemce byl přidán", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se přidat příjemce", "error");
        },
    });
};

export const useRemoveGlobalniPrijemceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (prijemceId) => IotService.removeGlobalniPrijemce(prijemceId),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.globalniPrijemci });
            showToast("Globální příjemce byl odebrán", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se odebrat příjemce", "error");
        },
    });
};

// =====================
// QUERIES - KONFIGURACE TYPU SENZORU
// =====================

export const useIotSenzorTypyQuery = () => {
    return useQuery<IotSenzorTypKonfig[], Error>({
        queryKey: IOT_QUERY_KEYS.senzorTypy,
        queryFn: () => IotService.getAllSenzorTypy(),
    });
};

export const useIotVychoziSablonyQuery = () => {
    return useQuery<{ popis: string; upresneni: string }, Error>({
        queryKey: IOT_QUERY_KEYS.vychoziSablony,
        queryFn: () => IotService.getVychoziSablony(),
        staleTime: Infinity,
    });
};

export const useIotSenzorTypQuery = (id: number) => {
    return useQuery<IotSenzorTypKonfig, Error>({
        queryKey: IOT_QUERY_KEYS.senzorTyp(id),
        queryFn: () => IotService.getSenzorTypById(id),
        enabled: !!id,
    });
};

// =====================
// MUTATIONS - KONFIGURACE TYPU SENZORU
// =====================

export const useCreateIotSenzorTypMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<IotSenzorTypKonfig, Error, CreateIotSenzorTypRequest>({
        mutationFn: (data) => IotService.createSenzorTyp(data),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.senzorTypy });
            showToast("Typ senzoru byl vytvořen", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vytvořit typ senzoru", "error");
        },
    });
};

export const useUpdateIotSenzorTypMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<IotSenzorTypKonfig, Error, { id: number; data: UpdateIotSenzorTypRequest }>({
        mutationFn: ({ id, data }) => IotService.updateSenzorTyp(id, data),

        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.senzorTypy });
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.senzorTyp(id) });
            showToast("Typ senzoru byl aktualizován", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat typ senzoru", "error");
        },
    });
};

export const useDeleteIotSenzorTypMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (id) => IotService.deleteSenzorTyp(id),

        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.senzorTypy });
            queryClient.removeQueries({ queryKey: IOT_QUERY_KEYS.senzorTyp(id) });
            showToast("Typ senzoru byl smazán", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat typ senzoru", "error");
        },
    });
};

export const useRefreshIotSenzorTypyCacheMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error>({
        mutationFn: () => IotService.refreshSenzorTypyCache(),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.senzorTypy });
            showToast("Cache typů senzorů byla obnovena", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se obnovit cache", "error");
        },
    });
};

// =====================
// QUERIES - MQTT CREDENTIALS
// =====================

export const useMqttCredentialsQuery = (enabled = true) => {
    return useQuery<MqttCredentialsResponse, Error>({
        queryKey: IOT_QUERY_KEYS.mqttCredentials,
        queryFn: () => IotService.getMqttCredentials(),
        enabled,
    });
};

// =====================
// MUTATIONS - MQTT CREDENTIALS
// =====================

/** Zobrazi AKTUALNI heslo (bez rotace) — pro pridani dalsiho senzoru bez rozbiti stavajicich. */
export const useShowMqttPasswordMutation = () => {
    const { showToast } = useToast();

    return useMutation<MqttCredentialsResponse, Error>({
        mutationFn: () => IotService.getMqttPassword(),

        onError: (error) => {
            showToast(error.message || "Nepodařilo se načíst heslo", "error");
        },
    });
};

export const useRegenerateMqttPasswordMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<MqttCredentialsResponse, Error>({
        mutationFn: () => IotService.regenerateMqttPassword(),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.mqttCredentials });
            showToast("MQTT heslo bylo regenerováno", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se regenerovat heslo", "error");
        },
    });
};

export const useDownloadZigbeeConfigMutation = () => {
    const { showToast } = useToast();

    return useMutation<string, Error, number>({
        mutationFn: (objektId) => IotService.downloadZigbeeConfig(objektId),

        onError: (error) => {
            showToast(error.message || "Nepodařilo se stáhnout konfiguraci", "error");
        },
    });
};

export const useDownloadBootstrapConfigMutation = () => {
    const { showToast } = useToast();

    return useMutation<string, Error, number>({
        mutationFn: (objektId) => IotService.downloadBootstrapConfig(objektId),

        onError: (error) => {
            showToast(error.message || "Nepodařilo se stáhnout bootstrap konfiguraci", "error");
        },
    });
};

// =====================
// QUERIES - CAPABILITIES
// =====================

export const useObjektCapabilitiesQuery = (objektId: number) => {
    return useQuery<SenzorCapabilities[], Error>({
        queryKey: IOT_QUERY_KEYS.capabilities(objektId),
        queryFn: () => IotService.getObjektCapabilities(objektId),
        enabled: !!objektId,
        staleTime: 5 * 60 * 1000, // 5 minut
    });
};

// =====================
// MUTATIONS - FEATURE COMMAND
// =====================

export const useSendFeatureCommandMutation = () => {
    const { showToast } = useToast();

    return useMutation<FeatureCommandResponse, Error, { senzorId: number; data: FeatureCommandRequest }>({
        mutationFn: ({ senzorId, data }) => IotService.sendFeatureCommand(senzorId, data),

        onSuccess: (result) => {
            showToast(result.message || "Příkaz odeslán", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se odeslat příkaz", "error");
        },
    });
};

// =====================
// QUERIES & MUTATIONS - DETEKCE GLOBALNI VYPNUTI
// =====================

export const useIotDetekceStavQuery = () => {
    return useQuery<IotDetekceStavResponse, Error>({
        queryKey: IOT_QUERY_KEYS.detekceStav,
        queryFn: () => IotService.getIotDetekceStav(),
        refetchInterval: 30 * 1000, // 30s pro banner
        staleTime: 10 * 1000,
    });
};

export const useVypnoutIotDetekciMutation = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation<IotDetekceStavResponse, Error, DisableDetekceRequest>({
        mutationFn: (data) => IotService.vypnoutIotDetekci(data),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.detekceStav });
            showToast("IoT/SIA detekce byla vypnuta", "warning");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vypnout detekci", "error");
        },
    });
};

export const useZapnoutIotDetekciMutation = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation<IotDetekceStavResponse, Error, void>({
        mutationFn: () => IotService.zapnoutIotDetekci(),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.detekceStav });
            showToast("IoT/SIA detekce byla zapnuta", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se zapnout detekci", "error");
        },
    });
};
